import { useState, useEffect, useCallback } from "react";
import { Bell } from "lucide-react";
import Dropdown from "components/dropdown";
import { useNavigate } from "react-router-dom";
import { useNotificationContext } from "contexts/NotificationContext";
import { useTranslation } from "react-i18next";

export const NotificationBell = () => {
  const navigate = useNavigate();
  const [isOpenDropdown, setIsOpenDropDown] = useState(false);

  // Use the global notification context
  const {
    unreadCount,
    notifications,
    loading,
    apiAvailable,
    error,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    clearError,
  } = useNotificationContext();

  // Fetch notifications when dropdown opens
  useEffect(() => {
    if (isOpenDropdown) {
      fetchNotifications();
    }
  }, [isOpenDropdown, fetchNotifications]);

  const getTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case "technical_note":
        return "bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400";
      case "success":
        return "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400";
      case "warning":
        return "bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400";
      case "error":
        return "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400";
      default:
        return "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400";
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case "technical_note":
        return "📝";
      case "success":
        return "✓";
      case "warning":
        return "⚠";
      case "error":
        return "✕";
      default:
        return "ℹ";
    }
  };

  const { t } = useTranslation();

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return t("notifications.justNow");
    if (diffInSeconds < 3600)
      return t("notifications.minutesAgo", {
        minutes: Math.floor(diffInSeconds / 60),
      });
    if (diffInSeconds < 86400)
      return t("notifications.hoursAgo", {
        hours: Math.floor(diffInSeconds / 3600),
      });
    if (diffInSeconds < 604800)
      return t("notifications.daysAgo", {
        days: Math.floor(diffInSeconds / 86400),
      });

    return date.toLocaleDateString();
  };

  return (
    <Dropdown
      setIsOpen={setIsOpenDropDown}
      isOpen={isOpenDropdown}
      button={
        <div className="relative flex w-max cursor-pointer items-center gap-3 rounded-lg p-2 transition-colors duration-200 hover:bg-gray-100 dark:hover:bg-navy-700">
          <Bell className="h-5 w-5 text-gray-600 dark:text-white" />
          {unreadCount > 0 && (
            <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-semibold text-white">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </div>
      }
      children={
        <div className="flex w-80 flex-col overflow-hidden rounded-xl border border-gray-100 bg-white shadow-xl dark:border-navy-600 dark:bg-navy-700">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3 dark:border-navy-600">
            <div>
              <h3 className="text-sm font-bold text-navy-700 dark:text-white">
                {t("notifications.title")}
              </h3>
              {unreadCount > 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t("notifications.unread", { count: 1 })}
                </p>
              )}
            </div>
            {/* {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs text-blue-600 transition-colors hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
              >
                Mark all read
              </button>
            )} */}
          </div>

          {/* Error Display */}
          {error && (
            <div className="border-b border-gray-100 bg-red-50 px-4 py-2 text-sm text-red-600 dark:border-navy-600 dark:bg-red-900/20 dark:text-red-400">
              <div className="flex items-center justify-between">
                <span>{error}</span>
                <button
                  onClick={clearError}
                  className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                >
                  ×
                </button>
              </div>
            </div>
          )}

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {!apiAvailable ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Bell className="mb-2 h-12 w-12 text-gray-300 dark:text-gray-600" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t("notifications.notificationsComingSoon")}
                </p>
              </div>
            ) : loading ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t("notifications.loadingNotifications")}
                </p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Bell className="mb-2 h-12 w-12 text-gray-300 dark:text-gray-600" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t("notifications.noNotifications")}
                </p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => {
                    if (notification.notification_status === "UNREAD") {
                      markAsRead(notification.id);
                    }
                    // Navigate to action link if available
                    if (notification.action_link) {
                      // Extract ID from action_link (e.g., /feedback?id=123 or /technical-notes/123)
                      let targetPath = notification.action_link;

                      // Ensure the path starts with /dashboard
                      if (!targetPath.startsWith("/dashboard")) {
                        targetPath = `/dashboard${targetPath}`;
                      }

                      // Navigate using React Router
                      navigate(targetPath);
                      setIsOpenDropDown(false);
                    }
                  }}
                  className={`cursor-pointer border-b border-gray-100 px-4 py-3 transition-colors duration-150 hover:bg-gray-50 dark:border-navy-600 dark:hover:bg-navy-600 ${
                    notification.notification_status === "UNREAD"
                      ? "bg-blue-50/50 dark:bg-navy-800/50"
                      : ""
                  }`}
                >
                  <div className="flex gap-3">
                    <div
                      className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm font-semibold ${getTypeColor(
                        notification.notification_type
                      )}`}
                    >
                      {getTypeIcon(notification.notification_type)}
                    </div>
                    <div className="flex-1 overflow-hidden">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="text-sm font-semibold text-navy-700 dark:text-white">
                          {notification.title}
                        </h4>
                        {notification.notification_status === "UNREAD" && (
                          <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-blue-600 dark:bg-blue-400"></span>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                        {formatTimestamp(notification.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {/* {notifications.length > 0 && (
            <div className="border-t border-gray-100 px-4 py-2 dark:border-navy-600">
              <button className="w-full text-center text-xs text-blue-600 transition-colors hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
                View all notifications
              </button>
            </div>
          )} */}
        </div>
      }
      classNames={"py-2 top-12 w-max right-2"}
    />
  );
};
